import React from "react";
import "./App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { format } from "date-fns";
import { Toaster } from "react-hot-toast";

// shadcn/ui + components
import { Nav } from "@/components/Navbar";
import { AuthProvider, useAuth } from "@/hooks/AuthContext";

// Pages
import NotificationsPage from "@/pages/NotificationsPage"
import MyDayPage from "@/pages/MyDay";
import LoginPage from "@/pages/LoginPage";
import CalendarPage from "@/pages/Calender";
import ProjectsDashboardPage from "@/pages/ProjectDashboard";
import TaskManagerPage from "@/pages/TaskManager";
import TeamPage from "@/pages/Team";
import PersonPage from "@/pages/Person";
import AdminHRPage from "@/pages/AdminHR";
import TeamDetailPage from "@/pages/TeamDetailPage";
import { ProjectDetailsPage } from "@/pages/ProjectDetail";
import PersonDetails from "@/pages/TeamDetailPagenew";
import LoadingOverlay from "@/components/LoadingOverlay";
import AdminSettings from "./pages/AdminSettings";
import CalendarContainer from "./pages/CalendarContainer";
import ResetPassword from "@/pages/ResetPassword";
import OpexManager from "@/pages/OpexManager";
import ReportsPage from "@/pages/Reports";
import SalaryReportsPage from "@/pages/SalaryReports";
import TeamRosterReportPage from "@/pages/TeamRosterReport";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Helpers
export const inr = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});
export const fmtDate = (d) => {
  if (!d) return "—";
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(d)))
      return new Date(`${d}T00:00:00+05:30`)
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .replace(/ /g, "-");
    const dd = new Date(d);
    return dd
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-");
  } catch {
    return String(d);
  }
};

export const fmtTimeIST = (iso) => {
  if (!iso) return "—";
  try {
    const t = new Date(iso);
    return t.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
};

export const ym = (jsDate) => format(jsDate, "yyyy-MM");

export const getStageColor = (stage) => {
  if (!stage) return "bg-gray-100 text-gray-800";
  const colors = [
    "bg-red-100 text-red-800",
    "bg-orange-100 text-orange-800",
    "bg-amber-100 text-amber-800",
    "bg-yellow-100 text-yellow-800",
    "bg-lime-100 text-lime-800",
    "bg-green-100 text-green-800",
    "bg-emerald-100 text-emerald-800",
    "bg-teal-100 text-teal-800",
    "bg-cyan-100 text-cyan-800",
    "bg-sky-100 text-sky-800",
    "bg-blue-100 text-blue-800",
    "bg-indigo-100 text-indigo-800",
    "bg-violet-100 text-violet-800",
    "bg-purple-100 text-purple-800",
    "bg-fuchsia-100 text-fuchsia-800",
    "bg-pink-100 text-pink-800",
    "bg-rose-100 text-rose-800",
  ];
  let hash = 0;
  for (let i = 0; i < stage.length; i++) {
    hash = stage.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Private Route
function PrivateRoute({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" replace />;
}

function Layout({ children }) {
  const location = useLocation();
    const disAllowedPatterns = [/^\/login$/, /^\/reset-password(\/.*)?$/];
    const hideNav = disAllowedPatterns.some((pattern) => pattern.test(location.pathname));

  return (
    <div className={hideNav ? "w-full" : "max-w-screen-xl mx-auto p-6"}>
      {!hideNav && <Nav />}
      <Toaster position="top-right" reverseOrder={false} />
      {children}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Layout>
            <LoadingOverlay />
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />

              {/* Secure */}
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <MyDayPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <PrivateRoute>
                  <CalendarContainer />
                </PrivateRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <PrivateRoute>
                  <ProjectsDashboardPage />
                </PrivateRoute>
              }
            />
              <Route
              path="/opex"
              element={
                <PrivateRoute>
                  <OpexManager />
                </PrivateRoute>
              }
            />
                <Route
              path="/notifications"
              element={
                <PrivateRoute>
                  <NotificationsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/projects/:id"
              element={
                <PrivateRoute>
                  <ProjectDetailsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/tasks"
              element={
                <PrivateRoute>
                  <TaskManagerPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/team"
              element={
                <PrivateRoute>
                  <TeamPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/team/:uid"
              element={
                <PrivateRoute>
                  <PersonPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/hr"
              element={
                <PrivateRoute>
                  <AdminHRPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/hr/:uid"
              element={
                <PrivateRoute>
                  <PersonDetails />
                </PrivateRoute>
              }
            />
  <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <AdminSettings/>
                </PrivateRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <PrivateRoute>
                  <ReportsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/reports/salary"
              element={
                <PrivateRoute>
                  <SalaryReportsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/reports/team-roster"
              element={
                <PrivateRoute>
                  <TeamRosterReportPage />
                </PrivateRoute>
              }
            />
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
